import {
  Album,
  AlbumDto,
  Mapper,
  SharingResult,
  SharingResultDto,
  UpdateUserFnReq,
  UpdateUserFnRes,
  User,
  UserDefaultAlbumStatus,
} from '@uploadcam/common';
import { Functions, httpsCallable } from 'firebase/functions';
import { GetUserByIdInteractor } from '../../../auth/domain/interactors/get-user-by-id.interactor';
import { GetAuthDtoInteractor } from '../../../auth/domain/interactors/get-auth-dto.interactor';

interface UpdateUserWithoutDefaultAlbumResult {
  user: User;
  defaultAlbumStatus: UserDefaultAlbumStatus;
}

interface UpdateUserWithDefaultAlbumResult extends UpdateUserWithoutDefaultAlbumResult {
  album: Album;
  sharingResult: SharingResult;
}

type UpdateUserResult = UpdateUserWithoutDefaultAlbumResult | UpdateUserWithDefaultAlbumResult;

export class UpdateUserInteractor {
  private readonly updateUserFn = httpsCallable<UpdateUserFnReq, UpdateUserFnRes>(
    this.functions,
    'updateUser',
  );

  constructor(
    private readonly albumMapper: Mapper<AlbumDto, Album>,
    private readonly functions: Functions,
    private readonly getAuthDto: GetAuthDtoInteractor,
    private readonly getUserById: GetUserByIdInteractor,
    private readonly sharingResultMapper: Mapper<SharingResultDto, SharingResult>,
  ) {}

  public async execute(data: Omit<UpdateUserFnReq, 'auth'>): Promise<UpdateUserResult> {
    const res = await this.updateUserFn({
      auth: await this.getAuthDto.execute(),
      ...data,
    });

    const { defaultAlbumStatus } = res.data;
    const user = await this.getUserById.execute(data.userId);

    if ('album' in res.data) {
      return {
        user,
        defaultAlbumStatus,
        album: this.albumMapper.map(res.data.album),
        sharingResult: this.sharingResultMapper.map(res.data.sharingResult),
      };
    } else {
      return {
        user,
        defaultAlbumStatus,
      };
    }
  }
}
